import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, money, formatDate, StatusPill, publicQuoteUrl, adminPdfUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Search, Eye, Copy, FileDown, MessageCircle, Trash2, Pencil, Files, ExternalLink, CheckCircle2, XCircle,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import OrcamentoEditor from "./OrcamentoEditor";

const STATUSES = ["todos", "rascunho", "enviado", "visualizados", "pendentes", "aprovado", "recusado", "finalizado"];

export default function Orcamentos() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(() => {
    const s = searchParams.get("status");
    return STATUSES.includes(s) ? s : "todos";
  });
  const [clientes, setClientes] = useState([]);
  const [duplicandoId, setDuplicandoId] = useState(null);
  const [duplicandoCliente, setDuplicandoCliente] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    const s = searchParams.get("status");
    setStatus(s && STATUSES.includes(s) ? s : "todos");
  }, [searchParams]);

  const abrirNovo = () => { setEditingId(null); setEditorOpen(true); };
  const abrirEditar = (id) => { setEditingId(id); setEditorOpen(true); };
  const fecharEditor = () => {
    setEditorOpen(false);
    setEditingId(null);
    if (searchParams.get("novo")) {
      searchParams.delete("novo");
      setSearchParams(searchParams, { replace: true });
    }
    load();
  };

  // Suporte a abrir "Novo orçamento" via query (?novo=1) — usado pelo botão do sidebar/mobile
  useEffect(() => {
    if (searchParams.get("novo") === "1" && !editorOpen) {
      setEditingId(null);
      setEditorOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = async () => {
    const { data } = await api.get("/orcamentos", {
      params: { q: q || undefined, status: status !== "todos" ? status : undefined },
    });
    setItems(data);
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/clientes");
      setClientes(data);
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status]);

  const copiar = (o) => {
    navigator.clipboard.writeText(publicQuoteUrl(o.token_publico));
    toast.success("Link copiado");
  };

  const whatsapp = (o) => {
    const nome = o.cliente?.nome || "";
    const numero = (o.cliente?.whatsapp || o.cliente?.telefone || "").replace(/\D/g, "");
    const msg = `Olá, ${nome}.\n\nSegue o orçamento nº ${o.numero_orcamento} referente aos serviços solicitados.\n\nVocê pode visualizar o orçamento através do link abaixo:\n\n${publicQuoteUrl(o.token_publico)}`;
    const url = numero
      ? `https://wa.me/${numero}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
    // marcar como enviado se estava em rascunho
    if (o.status === "rascunho") {
      api.patch(`/orcamentos/${o.id}/status`, { status: "enviado" }).then(load);
    }
  };

  const excluir = async (o) => {
    if (!window.confirm(`Excluir orçamento ${o.numero_orcamento}?`)) return;
    await api.delete(`/orcamentos/${o.id}`);
    toast.success("Orçamento excluído");
    load();
  };

  const aprovar = async (o) => {
    if (!window.confirm(`Aprovar orçamento ${o.numero_orcamento}?`)) return;
    try {
      await api.post(`/orcamentos/${o.id}/aprovar`);
      toast.success("Orçamento aprovado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const reprovar = async (o) => {
    const motivo = window.prompt(`Motivo da reprovação do orçamento ${o.numero_orcamento}? (opcional)`);
    if (motivo === null) return; // cancelou
    try {
      await api.post(`/orcamentos/${o.id}/recusar`, { motivo: motivo || null });
      toast.success("Orçamento reprovado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const reabrir = async (o) => {
    if (!window.confirm(`Reabrir orçamento ${o.numero_orcamento}?`)) return;
    try {
      await api.post(`/orcamentos/${o.id}/reabrir`);
      toast.success("Orçamento reaberto");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const cancelarAprovacao = async (o) => {
    if (!window.confirm(`Cancelar aprovação do orçamento ${o.numero_orcamento}? O status voltará para "enviado".`)) return;
    try {
      await api.post(`/orcamentos/${o.id}/reabrir`);
      toast.success("Aprovação cancelada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const finalizar = async (o) => {
    if (!window.confirm(`Marcar orçamento ${o.numero_orcamento} como finalizado? O serviço foi concluído?`)) return;
    try {
      await api.post(`/orcamentos/${o.id}/finalizar`);
      toast.success("Orçamento finalizado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const duplicar = async (o) => {
    setDuplicandoId(o.id);
    setDuplicandoCliente(o.cliente_id);
  };

  const confirmarDuplicacao = async () => {
    try {
      const { data } = await api.post(`/orcamentos/${duplicandoId}/duplicar`, {
        cliente_id: duplicandoCliente,
      });
      toast.success(`Orçamento ${data.numero_orcamento} criado`);
      setDuplicandoId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao duplicar");
    }
  };

  return (
    <div className="space-y-6" data-testid="orcamentos-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">
            Gestão
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Orçamentos</h1>
        </div>
        <button
          onClick={abrirNovo}
          data-testid="novo-orcamento-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bbh-btn-primary text-sm font-semibold self-start"
        >
          <Plus className="w-4 h-4" /> Novo Orçamento
        </button>
      </div>

      <div className="bbh-card p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            data-testid="orcamentos-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por número, cliente..."
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-zinc-200 bbh-input text-sm bg-white"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => {
                setStatus(s);
                if (s === "todos") searchParams.delete("status"); else searchParams.set("status", s);
                setSearchParams(searchParams, { replace: true });
              }}
              data-testid={`filter-status-${s}`}
              className={`px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wide ${
                status === s ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Card list (mobile) */}
      <div className="md:hidden space-y-3">
        {items.length === 0 && (
          <div className="bbh-card p-6 text-center text-zinc-500 text-sm">
            Nenhum orçamento encontrado
          </div>
        )}
        {items.map((o) => {
          const quitado = o.pagamento_status === "pago";
          return (
          <div
            key={o.id}
            className="bbh-card p-4"
            data-testid={`orcamento-card-${o.numero_orcamento}`}
            style={quitado ? { background: "#F0FDF4", borderColor: "#86EFAC" } : undefined}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="font-mono text-xs text-zinc-500">Nº {o.numero_orcamento}</div>
                <div className="font-medium truncate">{o.cliente?.nome || "-"}</div>
                <div className="text-xs text-zinc-500">{o.cliente?.codigo_cliente} · {formatDate(o.criado_em)}</div>
                {quitado && (
                  <div className="mt-1 text-xs font-semibold text-emerald-700" data-testid={`pago-info-${o.numero_orcamento}`}>
                    ✓ Pago {money(o.pagamento_total_pago || o.valor_total)}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading text-lg font-semibold">{money(o.valor_total)}</div>
                <StatusPill status={o.status} pagamentoStatus={o.pagamento_status} />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              {!quitado && (
                <button
                  onClick={() => abrirEditar(o.id)}
                  data-testid={`edit-mobile-${o.numero_orcamento}`}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md bg-zinc-900 text-white text-sm font-semibold"
                >
                  <Pencil className="w-4 h-4" /> Editar
                </button>
              )}
              <Link
                to={`/q/${o.token_publico}?admin=1`}
                data-testid={`visualizar-mobile-${o.numero_orcamento}`}
                className={`${quitado ? "flex-1 bg-zinc-900 text-white" : "flex-1 border border-zinc-300 text-zinc-800 hover:bg-zinc-50"} inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm font-semibold`}
              >
                <ExternalLink className="w-4 h-4" /> {quitado ? "Visualizar" : "Ver"}
              </Link>
            </div>
            <div className="flex gap-1 flex-wrap mt-2 pt-2 border-t border-zinc-100">
              {!quitado && o.status !== "aprovado" && o.status !== "finalizado" && (
                <button
                  onClick={() => aprovar(o)}
                  data-testid={`aprovar-mobile-${o.numero_orcamento}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Aprovar
                </button>
              )}
              {!quitado && o.status !== "aprovado" && o.status !== "recusado" && o.status !== "finalizado" && (
                <button
                  onClick={() => reprovar(o)}
                  data-testid={`reprovar-mobile-${o.numero_orcamento}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-rose-600 text-white text-xs font-semibold"
                >
                  <XCircle className="w-3.5 h-3.5" /> Reprovar
                </button>
              )}
              {!quitado && o.status === "aprovado" && (
                <>
                  <button
                    onClick={() => finalizar(o)}
                    data-testid={`finalizar-mobile-${o.numero_orcamento}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-indigo-600 text-white text-xs font-semibold"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Finalizar
                  </button>
                  <button
                    onClick={() => cancelarAprovacao(o)}
                    data-testid={`cancelar-aprovacao-mobile-${o.numero_orcamento}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-amber-500 text-white text-xs font-semibold"
                  >
                    <XCircle className="w-3.5 h-3.5" /> Cancelar aprovação
                  </button>
                </>
              )}
              {!quitado && o.status === "recusado" && (
                <button
                  onClick={() => reabrir(o)}
                  data-testid={`reabrir-mobile-${o.numero_orcamento}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-amber-500 text-white text-xs font-semibold"
                >
                  Reabrir
                </button>
              )}
              <IconBtn onClick={() => copiar(o)} icon={Copy} label="Link" testid={`copy-mobile-${o.numero_orcamento}`} />
              <IconBtn onClick={() => whatsapp(o)} icon={MessageCircle} label="WhatsApp" tone="success" testid={`whatsapp-mobile-${o.numero_orcamento}`} />
              <IconBtn href={adminPdfUrl(o.id)} icon={Eye} label="PDF" testid={`pdf-mobile-${o.numero_orcamento}`} />
              <IconBtn onClick={() => duplicar(o)} icon={Files} label="Duplicar" testid={`duplicar-mobile-${o.numero_orcamento}`} />
              {!quitado && (
                <IconBtn onClick={() => excluir(o)} icon={Trash2} label="Excluir" tone="danger" testid={`delete-mobile-${o.numero_orcamento}`} />
              )}
            </div>
          </div>
          );
        })}
      </div>

      {/* Table (desktop) */}
      <div className="hidden md:block bbh-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Nº</th>
              <th className="text-left px-4 py-3 font-semibold">Cliente</th>
              <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Data</th>
              <th className="text-right px-4 py-3 font-semibold">Valor</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold hidden xl:table-cell">Visualização</th>
              <th className="text-right px-4 py-3 font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {items.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-10 text-zinc-500">
                  Nenhum orçamento encontrado
                </td>
              </tr>
            )}
            {items.map((o) => (
              <tr key={o.id} className="bbh-row" data-testid={`orcamento-row-${o.numero_orcamento}`}>
                <td className="px-4 py-3 font-mono font-semibold">{o.numero_orcamento}</td>
                <td className="px-4 py-3">
                  <div>{o.cliente?.nome || "-"}</div>
                  <div className="text-xs text-zinc-500">{o.cliente?.codigo_cliente}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-zinc-600">{formatDate(o.criado_em)}</td>
                <td className="px-4 py-3 text-right font-heading font-semibold">{money(o.valor_total)}</td>
                <td className="px-4 py-3"><StatusPill status={o.status} pagamentoStatus={o.pagamento_status} /></td>
                <td className="px-4 py-3 hidden xl:table-cell text-xs text-zinc-500">
                  {o.primeira_visualizacao ? formatDate(o.primeira_visualizacao) : "-"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2 justify-end items-center">
                    <button
                      onClick={() => abrirEditar(o.id)}
                      data-testid={`edit-${o.numero_orcamento}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-zinc-900 text-white text-xs font-semibold"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Editar
                    </button>
                    <Link
                      to={`/q/${o.token_publico}?admin=1`}
                      data-testid={`visualizar-${o.numero_orcamento}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-zinc-300 text-zinc-800 text-xs font-semibold hover:bg-zinc-50"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Ver
                    </Link>
                    {o.status !== "aprovado" && o.status !== "finalizado" && (
                      <button
                        onClick={() => aprovar(o)}
                        data-testid={`aprovar-${o.numero_orcamento}`}
                        title="Aprovar"
                        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Aprovar
                      </button>
                    )}
                    {o.status !== "aprovado" && o.status !== "recusado" && o.status !== "finalizado" && (
                      <button
                        onClick={() => reprovar(o)}
                        data-testid={`reprovar-${o.numero_orcamento}`}
                        title="Reprovar"
                        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Reprovar
                      </button>
                    )}
                    {o.status === "aprovado" && (
                      <>
                        <button
                          onClick={() => finalizar(o)}
                          data-testid={`finalizar-${o.numero_orcamento}`}
                          title="Finalizar"
                          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Finalizar
                        </button>
                        <button
                          onClick={() => cancelarAprovacao(o)}
                          data-testid={`cancelar-aprovacao-${o.numero_orcamento}`}
                          title="Cancelar aprovação"
                          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold"
                        >
                          <XCircle className="w-3.5 h-3.5" /> Cancelar aprovação
                        </button>
                      </>
                    )}
                    {o.status === "recusado" && (
                      <button
                        onClick={() => reabrir(o)}
                        data-testid={`reabrir-${o.numero_orcamento}`}
                        title="Reabrir"
                        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold"
                      >
                        Reabrir
                      </button>
                    )}
                    <IconBtn onClick={() => copiar(o)} icon={Copy} label="Link" testid={`copy-${o.numero_orcamento}`} />
                    <IconBtn href={adminPdfUrl(o.id)} icon={Eye} label="PDF" testid={`view-pdf-${o.numero_orcamento}`} />
                    <IconBtn href={adminPdfUrl(o.id, true)} icon={FileDown} label="Baixar" testid={`download-pdf-${o.numero_orcamento}`} />
                    <IconBtn onClick={() => whatsapp(o)} icon={MessageCircle} label="WhatsApp" tone="success" testid={`whatsapp-${o.numero_orcamento}`} />
                    <IconBtn onClick={() => duplicar(o)} icon={Files} label="Duplicar" testid={`duplicate-${o.numero_orcamento}`} />
                    <IconBtn onClick={() => excluir(o)} icon={Trash2} label="Excluir" tone="danger" testid={`delete-${o.numero_orcamento}`} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sheet lateral do editor */}
      <Sheet open={editorOpen} onOpenChange={(v) => { if (!v) fecharEditor(); else setEditorOpen(true); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-4xl p-0 overflow-y-auto"
          data-testid="orcamento-editor-sheet"
          style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        >
          <SheetTitle className="sr-only">{editingId ? "Editar orçamento" : "Novo orçamento"}</SheetTitle>
          <SheetDescription className="sr-only">
            {editingId ? "Painel lateral para editar o orçamento selecionado." : "Painel lateral para criar um novo orçamento."}
          </SheetDescription>
          <div className="p-4 sm:p-6">
            {editorOpen && (
              <OrcamentoEditor
                key={editingId || "novo"}
                overrideId={editingId}
                inSheet
                onClose={fecharEditor}
                onSaved={() => { /* keep sheet open, list refreshes on close */ }}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {duplicandoId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Duplicar orçamento</h3>
            <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
              Cliente destino
            </label>
            <select
              value={duplicandoCliente}
              onChange={(e) => setDuplicandoCliente(e.target.value)}
              data-testid="duplicar-cliente-select"
              className="mt-1 w-full px-3 py-2.5 rounded-md border border-zinc-200 bbh-input text-sm bg-white"
            >
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.codigo_cliente} - {c.nome}
                </option>
              ))}
            </select>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setDuplicandoId(null)}
                className="flex-1 px-4 py-2.5 rounded-md border border-zinc-200 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarDuplicacao}
                data-testid="confirmar-duplicacao-btn"
                className="flex-1 px-4 py-2.5 rounded-md bbh-btn-primary font-semibold"
              >
                Duplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, to, href, label, tone, testid }) {
  const cls = `inline-flex items-center gap-1 px-2 py-1.5 rounded-md border text-xs font-semibold transition-colors ${
    tone === "danger" ? "border-rose-200 text-rose-600 hover:bg-rose-50" :
    tone === "success" ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" :
    "border-zinc-200 text-zinc-700 hover:bg-zinc-100"
  }`;
  const inner = <><Icon className="w-3.5 h-3.5" /> {label}</>;
  if (to) return <Link to={to} className={cls} title={label} data-testid={testid}>{inner}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls} title={label} data-testid={testid}>{inner}</a>;
  return <button onClick={onClick} className={cls} title={label} data-testid={testid}>{inner}</button>;
}
